import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiUpload, FiPlay, FiDownload, FiCopy, FiCheck, FiKey, FiRefreshCw } from 'react-icons/fi';
import { useHtmlDataTheme } from '../../../services/useHtmlDataTheme';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { usePaginatedAasApiKeys } from '../../../helpers/api/aas_api_keys/aas_api_keys';
import './docs.page.css';
import { Select } from '../../ComponnentsUI/common/Select';

const DocsPage: React.FC = () => {
  const { t } = useTranslation('Docs');
  const { t: tApi } = useTranslation('ApiAsService');
  const [activeTab, setActiveTab] = useState('process');
    const [expandedTests, setExpandedTests] = useState<Record<string, boolean>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
    const theme = useHtmlDataTheme();
    const [testForm, setTestForm] = useState({
        apiKey: '',
        selectedApiKeyId: '',
        currentEndpoint: 'process',
        // Endpoint-specific payloads
        payloads: {
            process: {
                pixelSpacing: '1.0',
                pixelSpacingUnit: 'mm',
                asyncProcess: false,
                selectedFile: null as File | null
            },
            response: {
                requestId: ''
            },
            class: {
                labels: '1,2,3'
            },
            upload_exam: {
                pixelSpacing: '0.2',
                pixelSpacingUnit: 'mm',
                sweepFiles: [null, null, null, null, null, null, null] as (File | null)[]
            },
            new_exam: {
                // No specific payload needed
            },
            upload: {
                examId: '',
                sweepIndex: '1',
                pixelSpacing: '1.0',
                pixelSpacingUnit: 'mm',
                beginProcess: false,
                selectedSweepFile: null as File | null
            },
            reports: {
                examId: ''
            }
        },
        // Endpoint-specific responses
        responses: {
            process: null as any,
            response: null as any,
            class: null as any,
            upload_exam: null as any,
            new_exam: null as any,
            upload: null as any,
            reports: null as any
        }
    });
    const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
    const [showApiKeyDropdown, setShowApiKeyDropdown] = useState(false);

    // Fetch API keys
    const { data: apiKeysData, refetch: refetchApiKeys } = usePaginatedAasApiKeys(1, 100) as {
        data: any;
        refetch: () => void;
    };

    useDocumentTitle('DEEPECHO - API Documentation');

    // Test API functions
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setTestForm(prev => ({
                ...prev,
                payloads: {
                    ...prev.payloads,
                    process: { ...prev.payloads.process, selectedFile: file }
                }
            }));
        }
    };

    const handleSweepFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setTestForm(prev => ({
                ...prev,
                payloads: {
                    ...prev.payloads,
                    upload: { ...prev.payloads.upload, selectedSweepFile: file }
                }
            }));
        }
    };

    const handleUploadExamSweepFile = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file == null) return;
        setTestForm(prev => {
            const next = [...(prev.payloads.upload_exam.sweepFiles || [])];
            while (next.length < 7) next.push(null);
            next[index] = file;
            return {
                ...prev,
                payloads: {
                    ...prev.payloads,
                    upload_exam: { ...prev.payloads.upload_exam, sweepFiles: next }
                }
            };
        });
        event.target.value = '';
    };

    const handleUploadExamAllFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = event.target.files;
        if (!fileList?.length) return;
        const files = Array.from(fileList).slice(0, 7);
        const sweepFiles: (File | null)[] = [...files];
        while (sweepFiles.length < 7) sweepFiles.push(null);
        setTestForm(prev => ({
            ...prev,
            payloads: {
                ...prev.payloads,
                upload_exam: { ...prev.payloads.upload_exam, sweepFiles }
            }
        }));
        event.target.value = '';
    };

    const executeTest = async (endpoint: string) => {
        if (!testForm.apiKey) {
            alert('Please provide API key');
            return;
        }

        const currentPayload = testForm.payloads[endpoint as keyof typeof testForm.payloads];
        
        // Validate endpoint-specific requirements
        if (endpoint === 'process' && !(currentPayload as any).selectedFile) {
            alert('Please select a file for the /process endpoint');
            return;
        }
        if (endpoint === 'response' && !(currentPayload as any).requestId) {
            alert('Please provide a request ID for the /response endpoint');
            return;
        }
        if (endpoint === 'class' && !(currentPayload as any).labels) {
            alert('Please provide labels for the /class endpoint');
            return;
        }
        if (endpoint === 'upload_exam') {
            const files = (currentPayload as any).sweepFiles;
            if (!files || files.length !== 7 || files.some((f: File | null) => !f)) {
                alert('Please provide all 7 sweep files for the /upload_exam endpoint');
                return;
            }
        }
        if (endpoint === 'upload' && (!(currentPayload as any).examId || !(currentPayload as any).selectedSweepFile)) {
            alert('Please provide an exam ID and file for the /upload endpoint');
            return;
        }
        if (endpoint === 'reports' && !(currentPayload as any).examId) {
            alert('Please provide an exam ID for the /reports endpoint');
            return;
        }

        setIsLoading(prev => ({ ...prev, [endpoint]: true }));

        try {
            // Get the correct backend URL
            const backendUrl = (window as any).RUNTIME_ENV?.BACKEND_URL ?? 
                            process.env.BACKEND_URL ?? 
                            'http://localhost:8000/api/v1';

            let response;
            let requestBody;

            if (endpoint === 'process') {
                // Process endpoint - file upload
                const formData = new FormData();
                formData.append('files', (currentPayload as any).selectedFile);
                // Note: pixel_spacing and pixel_spacing_unit are query parameters, not form data
                requestBody = formData;
            } else if (endpoint === 'response') {
                // Response endpoint - query parameter, no body
                requestBody = null;
            } else if (endpoint === 'class') {
                // Class endpoint - labels array directly in body
                const labelsArray = (currentPayload as any).labels.split(',').map((l: string) => parseInt(l.trim())).filter((l: number) => !isNaN(l));
                requestBody = JSON.stringify(labelsArray);
            } else if (endpoint === 'upload_exam') {
                const formData = new FormData();
                const payload = (currentPayload as any);
                const ps = parseFloat(payload.pixelSpacing) || 0.2;
                const unit = payload.pixelSpacingUnit || 'mm';
                const sweepsMeta = [1, 2, 3, 4, 5, 6, 7].map((i) => ({
                    index: i,
                    pixel_spacing: ps,
                    pixel_spacing_unit: unit
                }));
                // Append files first so multipart parser receives them before payload (avoids "Skipping data after last boundary" issues)
                (payload.sweepFiles as (File | null)[]).forEach((file: File | null, i: number) => {
                    if (file) formData.append(`sweep_${i + 1}`, file, file.name || `sweep_${i + 1}`);
                });
                formData.append('payload', JSON.stringify({ sweeps: sweepsMeta }));
                requestBody = formData;
            } else if (endpoint === 'new_exam') {
                // New exam endpoint - no body needed
                requestBody = null;
            } else if (endpoint === 'upload') {
                // Upload sweep endpoint - form data with file only, other params as query params
                const formData = new FormData();
                formData.append('file', (currentPayload as any).selectedSweepFile);
                // Note: pixel_spacing and pixel_spacing_unit are query parameters, not form data
                requestBody = formData;
            } else if (endpoint === 'reports') {
                // Reports endpoint - no body, aas_bs_exam_id in URL
                requestBody = null;
            }

            const headers: Record<string, string> = {
                'X-API-Key': testForm.apiKey,
            };

            // Add Content-Type for JSON endpoints
            if (endpoint === 'class') {
                headers['Content-Type'] = 'application/json';
            }

            // Build URL with query parameters
            let url = `${backendUrl}/aas/${endpoint}`;
            if (endpoint === 'response') {
                url += `?id=${encodeURIComponent((currentPayload as any).requestId)}`;
            } else if (endpoint === 'reports') {
                const examId = String((currentPayload as any).examId ?? '').trim().replace(/^["']|["']$/g, '');
                url = `${backendUrl}/aas_bs/reports/${encodeURIComponent(examId)}`;
            } else if (endpoint === 'upload_exam') {
                url = `${backendUrl}/aas_bs/upload_exam`;
            } else if (endpoint === 'new_exam') {
                url = `${backendUrl}/aas_bs/new_exam`;
            } else if (endpoint === 'upload') {
                // Build query parameters for upload endpoint
                const queryParams = new URLSearchParams();
                queryParams.append('aas_bs_exam_id', (currentPayload as any).examId);
                queryParams.append('sweep_index', (currentPayload as any).sweepIndex);
                queryParams.append('begin_process', (currentPayload as any).beginProcess.toString());
                queryParams.append('pixel_spacing', (currentPayload as any).pixelSpacing);
                queryParams.append('pixel_spacing_unit', (currentPayload as any).pixelSpacingUnit);
                url = `${backendUrl}/aas_bs/upload?${queryParams.toString()}`;
            } else if (endpoint === 'process') {
                // Add query parameters for process endpoint
                const queryParams = new URLSearchParams();
                queryParams.append('async_process', (currentPayload as any).asyncProcess.toString());
                queryParams.append('pixel_spacing', (currentPayload as any).pixelSpacing);
                queryParams.append('pixel_spacing_unit', (currentPayload as any).pixelSpacingUnit);
                url += `?${queryParams.toString()}`;
            }

            // Determine the correct HTTP method based on the endpoint
            let method = 'POST'; // Default to POST
            if (endpoint === 'reports') {
                method = 'GET';
            }

            response = await fetch(url, {
                method: method,
                headers,
                body: requestBody
            });

            const result = await response.json();
            
            // Store response for this specific endpoint
            setTestForm(prev => ({
                ...prev,
                responses: {
                    ...prev.responses,
                    [endpoint]: {
                        status: response.status,
                        statusText: response.statusText,
                        data: result,
                        headers: Object.fromEntries(response.headers.entries())
                    }
                }
            }));
        } catch (error) {
            // Store error for this specific endpoint
            setTestForm(prev => ({
                ...prev,
                responses: {
                    ...prev.responses,
                    [endpoint]: {
                        error: error instanceof Error ? error.message : 'Unknown error occurred'
                    }
                }
            }));
        } finally {
            setIsLoading(prev => ({ ...prev, [endpoint]: false }));
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const toggleTestExpansion = (endpoint: string) => {
        setExpandedTests(prev => ({
            ...prev,
            [endpoint]: !prev[endpoint]
        }));
    };

    const toggleSectionExpansion = (sectionKey: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    const setTestEndpoint = (endpoint: string) => {
        setTestForm(prev => ({
            ...prev,
            currentEndpoint: endpoint
        }));
    };

    const renderTestForm = (endpointKey: string) => {
        const isProcessEndpoint = endpointKey === 'process';
        const isResponseEndpoint = endpointKey === 'response';
        const isClassEndpoint = endpointKey === 'class';
        const isUploadExamEndpoint = endpointKey === 'upload_exam';
        const isNewExamEndpoint = endpointKey === 'new_exam';
        const isUploadSweepEndpoint = endpointKey === 'upload';
        const isReportsEndpoint = endpointKey === 'reports';

        const currentPayload = testForm.payloads[endpointKey as keyof typeof testForm.payloads];
        const currentResponse = testForm.responses[endpointKey as keyof typeof testForm.responses];
        const currentLoading = isLoading[endpointKey] || false;

        return (
            <div className="space-y-4">
                {/* Current Endpoint Header */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            Testing: {endpointKey.toUpperCase()} endpoint
                        </span>
                    </div>
                </div>

                {/* API Key Selection */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        API Key *
                    </label>
                    <div className="relative api-key-dropdown-container">
                        <div className="flex">
                            <input
                                type="password"
                                value={testForm.apiKey}
                                onChange={(e) => setTestForm(prev => ({ ...prev, apiKey: e.target.value, selectedApiKeyId: '' }))}
                                placeholder="Enter your API key or select from existing"
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                            <button
                                type="button"
                                onClick={() => setShowApiKeyDropdown(!showApiKeyDropdown)}
                                className="px-3 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md bg-gray-50 dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300"
                                title="Select from existing API keys"
                            >
                                <FiKey className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => refetchApiKeys()}
                                className="px-3 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md bg-gray-50 dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300"
                                title="Refresh API keys"
                            >
                                <FiRefreshCw className="h-4 w-4" />
                            </button>
                        </div>
                        
                        {/* API Key Dropdown */}
                        {showApiKeyDropdown && (
                            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                {activeApiKeys.length > 0 ? (
                                    activeApiKeys.map((apiKey: any) => (
                                        <button
                                            key={apiKey.id}
                                            onClick={() => handleApiKeySelect(apiKey)}
                                            className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="font-medium">{apiKey.name}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        Created: {new Date(apiKey.created_at).toLocaleDateString()}
                                                    </div>
                                                </div>
                                                <div className="text-xs text-gray-400 dark:text-gray-500">
                                                    {apiKey.value.slice(-4)}
                                                </div>
                                            </div>
                                        </button>
                                    ))
                                ) : (
                                    <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                        <div className="flex items-center gap-2">
                                            <FiKey className="h-4 w-4" />
                                            <span>{tApi('docs.empty.noApiKeys')}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {/* Selected API Key Info */}
                    {testForm.selectedApiKeyId && (
                        <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-xs text-green-700 dark:text-green-300">
                            <div className="flex items-center gap-2">
                                <FiCheck className="h-3 w-3" />
                                <span>
                                    Using selected API key: {activeApiKeys.find(key => key.id === testForm.selectedApiKeyId)?.name || 'Unknown'}
                                </span>
                                <button
                                    onClick={() => setTestForm(prev => ({ ...prev, apiKey: '', selectedApiKeyId: '' }))}
                                    className="ml-auto text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Process endpoint specific fields */}
                {isProcessEndpoint && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Pixel Spacing
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={(currentPayload as any).pixelSpacing}
                                    onChange={(e) => setTestForm(prev => ({ 
                                        ...prev, 
                                        payloads: { 
                                            ...prev.payloads, 
                                            process: { ...prev.payloads.process, pixelSpacing: e.target.value } 
                                        } 
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Pixel Spacing Unit
                                </label>
                                <Select
                                    value={(currentPayload as any).pixelSpacingUnit}
                                    onChange={(e) => setTestForm(prev => ({ 
                                        ...prev, 
                                        payloads: { 
                                            ...prev.payloads, 
                                            process: { ...prev.payloads.process, pixelSpacingUnit: e.target.value } 
                                        } 
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    options={[
                                      { value: 'mm', label: 'mm' },
                                      { value: 'cm', label: 'cm' },
                                    ]}
                                />
                            </div>
                        </div>

                        <div className="flex items-center">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={(currentPayload as any).asyncProcess}
                                    onChange={(e) => setTestForm(prev => ({ 
                                        ...prev, 
                                        payloads: { 
                                            ...prev.payloads, 
                                            process: { ...prev.payloads.process, asyncProcess: e.target.checked } 
                                        } 
                                    }))}
                                    className="mr-2"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">Async Process</span>
                            </label>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                File Upload *
                            </label>
                            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                                <input
                                    type="file"
                                    onChange={handleFileSelect}
                                    accept="image/*,.dcm,.dicom"
                                    className="hidden"
                                    id="file-upload"
                                />
                                <label htmlFor="file-upload" className="cursor-pointer">
                                    <FiUpload className="mx-auto h-12 w-12 text-gray-400" />
                                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                        {(currentPayload as any).selectedFile ? (currentPayload as any).selectedFile.name : 'Click to upload or drag and drop'}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                        PNG, JPG, DICOM files supported
                                    </p>
                                </label>
                            </div>
                        </div>
                    </>
                )}

                {/* Response endpoint specific fields */}
                {isResponseEndpoint && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Request ID *
                        </label>
                        <input
                            type="text"
                            value={(currentPayload as any).requestId}
                            onChange={(e) => setTestForm(prev => ({ 
                                ...prev, 
                                payloads: { 
                                    ...prev.payloads, 
                                    response: { ...prev.payloads.response, requestId: e.target.value } 
                                } 
                            }))}
                            placeholder="Enter the request ID from async process"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            This should be the ID returned from an async /process request
                        </p>
                    </div>
                )}

                {/* Class endpoint specific fields */}
                {isClassEndpoint && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Structure Labels *
                        </label>
                        <input
                            type="text"
                            value={(currentPayload as any).labels}
                            onChange={(e) => setTestForm(prev => ({ 
                                ...prev, 
                                payloads: { 
                                    ...prev.payloads, 
                                    class: { ...prev.payloads.class, labels: e.target.value } 
                                } 
                            }))}
                            placeholder="Enter comma-separated label IDs (e.g., 1,2,3,4)"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Enter comma-separated structure label IDs (e.g., 1,2,3,4)
                        </p>
                    </div>
                )}

                {/* Upload Exam (7 sweeps) endpoint specific fields */}
                {isUploadExamEndpoint && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Pixel Spacing (for all sweeps, if non-DICOM)
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={(currentPayload as any).pixelSpacing}
                                    onChange={(e) => setTestForm(prev => ({
                                        ...prev,
                                        payloads: {
                                            ...prev.payloads,
                                            upload_exam: { ...prev.payloads.upload_exam, pixelSpacing: e.target.value }
                                        }
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Pixel Spacing Unit
                                </label>
                                <Select
                                    value={(currentPayload as any).pixelSpacingUnit}
                                    onChange={(e) => setTestForm(prev => ({
                                        ...prev,
                                        payloads: {
                                            ...prev.payloads,
                                            upload_exam: { ...prev.payloads.upload_exam, pixelSpacingUnit: e.target.value }
                                        }
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    options={[
                                      { value: 'mm', label: 'mm' },
                                      { value: 'cm', label: 'cm' },
                                    ]}
                                />
                            </div>
                        </div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Sweep files (7 required) *
                        </label>
                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 mb-3">
                            <input
                                type="file"
                                accept="image/*,.dcm,.dicom,video/*"
                                className="hidden"
                                id="upload_exam_all_seven"
                                multiple
                                onChange={handleUploadExamAllFiles}
                            />
                            <label
                                htmlFor="upload_exam_all_seven"
                                className="flex items-center gap-2 cursor-pointer text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                            >
                                <FiUpload className="h-5 w-5 flex-shrink-0" />
                                Select all 7 sweep files at once (order = Sweep 1 → 7)
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Or pick files one by one below.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                                <div key={i} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Sweep {i}</label>
                                    <input
                                        type="file"
                                        accept="image/*,.dcm,.dicom,video/*"
                                        className="hidden"
                                        id={`upload_exam_sweep_${i}`}
                                        onChange={(e) => handleUploadExamSweepFile(i - 1, e)}
                                    />
                                    <label
                                        htmlFor={`upload_exam_sweep_${i}`}
                                        className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                                    >
                                        <FiUpload className="h-4 w-4 flex-shrink-0" />
                                        <span className="truncate">
                                            {(currentPayload as any).sweepFiles?.[i - 1]?.name ?? `Select file ${i}`}
                                        </span>
                                    </label>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            DICOM or video for each sweep. Inference is submitted automatically after upload.
                        </p>
                    </>
                )}

                {/* Upload Sweep endpoint specific fields */}
                {isUploadSweepEndpoint && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Exam ID *
                                </label>
                                <input
                                    type="text"
                                    value={(currentPayload as any).examId}
                                    onChange={(e) => setTestForm(prev => ({ 
                                        ...prev, 
                                        payloads: { 
                                            ...prev.payloads, 
                                            upload: { ...prev.payloads.upload, examId: e.target.value } 
                                        } 
                                    }))}
                                    placeholder="Enter exam ID"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Sweep Index *
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={(currentPayload as any).sweepIndex}
                                    onChange={(e) => setTestForm(prev => ({ 
                                        ...prev, 
                                        payloads: { 
                                            ...prev.payloads, 
                                            upload: { ...prev.payloads.upload, sweepIndex: e.target.value } 
                                        } 
                                    }))}
                                    placeholder="Enter sweep index (1, 2, 3, ...)"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Pixel Spacing
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={(currentPayload as any).pixelSpacing}
                                    onChange={(e) => setTestForm(prev => ({ 
                                        ...prev, 
                                        payloads: { 
                                            ...prev.payloads, 
                                            upload: { ...prev.payloads.upload, pixelSpacing: e.target.value } 
                                        } 
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Pixel Spacing Unit
                                </label>
                                <Select
                                    value={(currentPayload as any).pixelSpacingUnit}
                                    onChange={(e) => setTestForm(prev => ({ 
                                        ...prev, 
                                        payloads: { 
                                            ...prev.payloads, 
                                            upload: { ...prev.payloads.upload, pixelSpacingUnit: e.target.value } 
                                        } 
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    options={[
                                      { value: 'mm', label: 'mm' },
                                      { value: 'cm', label: 'cm' },
                                    ]}
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={(currentPayload as any).beginProcess}
                                    onChange={(e) => setTestForm(prev => ({ 
                                        ...prev, 
                                        payloads: { 
                                            ...prev.payloads, 
                                            upload: { ...prev.payloads.upload, beginProcess: e.target.checked } 
                                        } 
                                    }))}
                                    className="mr-2"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">Begin Process Immediately</span>
                            </label>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Sweep File Upload *
                            </label>
                            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                                <input
                                    type="file"
                                    onChange={handleSweepFileSelect}
                                    accept="image/*,.dcm,.dicom,video/*"
                                    className="hidden"
                                    id="sweep-file-upload"
                                />
                                <label htmlFor="sweep-file-upload" className="cursor-pointer">
                                    <FiUpload className="mx-auto h-12 w-12 text-gray-400" />
                                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                        {(currentPayload as any).selectedSweepFile ? (currentPayload as any).selectedSweepFile.name : 'Click to upload sweep file'}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                        DICOM, images, or video files supported
                                    </p>
                                </label>
                            </div>
                        </div>
                    </>
                )}

                {/* Reports endpoint specific fields */}
                {isReportsEndpoint && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Exam ID *
                        </label>
                        <input
                            type="text"
                            value={(currentPayload as any).examId}
                            onChange={(e) => setTestForm(prev => ({ 
                                ...prev, 
                                payloads: { 
                                    ...prev.payloads, 
                                    reports: { ...prev.payloads.reports, examId: e.target.value } 
                                } 
                            }))}
                            placeholder="Enter exam ID to get report"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            This will retrieve the exam report and segmentation results
                        </p>
                    </div>
                )}

                {/* Execute Test Button */}
                <button
                    onClick={() => executeTest(endpointKey)}
                    disabled={
                        currentLoading || 
                        !testForm.apiKey || 
                        (isProcessEndpoint && !(currentPayload as any).selectedFile) ||
                        (isResponseEndpoint && !(currentPayload as any).requestId) ||
                        (isClassEndpoint && !(currentPayload as any).labels) ||
                        (isUploadExamEndpoint && (!(currentPayload as any).sweepFiles || (currentPayload as any).sweepFiles.length !== 7 || (currentPayload as any).sweepFiles.some((f: File | null) => !f))) ||
                        (isUploadSweepEndpoint && (!(currentPayload as any).examId || !(currentPayload as any).selectedSweepFile)) ||
                        (isReportsEndpoint && !(currentPayload as any).examId)
                    }
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                >
                    {currentLoading ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Testing...
                        </>
                    ) : (
                        <>
                            <FiPlay className="h-4 w-4" />
                            Execute Test
                        </>
                    )}
                </button>

                {/* Test Results - Scrollable */}
                {currentResponse && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                        <h4 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Test Results</h4>
                        
                        <div className="mb-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                currentResponse.status >= 200 && currentResponse.status < 300 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                                {currentResponse.status} {currentResponse.statusText}
                            </span>
                        </div>

                        <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                            {currentResponse.error ? (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 m-4">
                                    <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">Error</h4>
                                    <p className="text-sm text-red-700 dark:text-red-300">{currentResponse.error}</p>
                                </div>
                            ) : (
                                <div className="p-4">
                                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Response Data</h4>
                                    <pre className="bg-gray-50 dark:bg-gray-700 p-3 rounded text-xs overflow-x-auto">
                                        {JSON.stringify(currentResponse.data, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Get available API keys - the backend returns data in 'items' field, not 'data'
    const availableApiKeys = apiKeysData?.items || [];
    const activeApiKeys = availableApiKeys.filter((key: any) => !key.is_disabled);
    
    const handleApiKeySelect = (apiKey: any) => {
        setTestForm(prev => ({
            ...prev,
            apiKey: apiKey.value,
            selectedApiKeyId: apiKey.id
        }));
        setShowApiKeyDropdown(false);
    };

    // Refresh API keys when testing tab is opened and auto-select first available key
    useEffect(() => {
        if (activeTab === 'testing') {
            refetchApiKeys();
        }
    }, [activeTab, refetchApiKeys]);

    // Auto-select first API key when available
    useEffect(() => {
        if (activeApiKeys.length > 0 && !testForm.apiKey && !testForm.selectedApiKeyId) {
            const firstKey = activeApiKeys[0];
            setTestForm(prev => ({
                ...prev,
                apiKey: firstKey.value,
                selectedApiKeyId: firstKey.id
            }));
        }
    }, [activeApiKeys, testForm.apiKey, testForm.selectedApiKeyId]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (showApiKeyDropdown && !target.closest('.api-key-dropdown-container')) {
                setShowApiKeyDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showApiKeyDropdown]);

  const processEndpoints = useMemo(() => [
    {
      endpoint: '/process',
      method: 'POST',
      title: t('endpoints.process.title'),
      description: t('endpoints.process.description'),
      parameters: [
        { name: 'files', type: 'File', required: true, description: 'Medical image file to process' },
        { name: 'pixel_spacing', type: 'float', required: false, description: 'Pixel spacing value (default: -1 for auto-detection)' },
        { name: 'pixel_spacing_unit', type: 'enum', required: false, description: 'Unit for pixel spacing (mm or cm, default: mm)' },
        { name: 'async_process', type: 'boolean', required: false, description: 'Process asynchronously (default: false)' }
      ],
      responses: [
        { status: 200, description: 'Processing completed successfully', example: '{ "segmentation": {...}, "metadata": {...} }' },
        { status: 202, description: 'Processing started asynchronously', example: '{ "id": "request-id" }' },
        { status: 400, description: 'Invalid file format or parameters' },
        { status: 422, description: 'Validation error' }
      ]
    },
    {
      endpoint: '/response',
      method: 'POST',
      title: t('endpoints.response.title'),
      description: t('endpoints.response.description'),
      parameters: [
        { name: 'id', type: 'string', required: true, description: 'Request ID from async process' }
      ],
      responses: [
        { status: 200, description: 'Response retrieved successfully', example: '{ "status": "completed", "result": {...} }' },
        { status: 202, description: 'Processing still in progress', example: '{ "status": "processing" }' },
        { status: 404, description: 'Request ID not found' }
      ]
    },
    {
      endpoint: '/class',
      method: 'POST',
      title: t('endpoints.class.title'),
      description: t('endpoints.class.description'),
      parameters: [
        { name: 'labels', type: 'array', required: true, description: 'Array of structure label IDs to classify' }
      ],
      responses: [
        { status: 200, description: 'Classification completed', example: '{ "classifications": [...] }' },
        { status: 400, description: 'Invalid labels provided' }
      ]
    }
  ], [t]);

  const blindSweepsEndpoints = useMemo(() => [
    {
      endpoint: '/upload_exam',
      method: 'POST',
      title: t('endpoints.uploadExam.title'),
      description: t('endpoints.uploadExam.description'),
      parameters: [
        { name: 'payload', type: 'string', required: true, description: 'JSON: { "sweeps": [ { "index": 1, "pixel_spacing": 0.2, "pixel_spacing_unit": "mm" }, ... ] } — exactly 7 entries, indices 1–7' },
        { name: 'sweep_1', type: 'File', required: true, description: 'Sweep 1 file (DICOM or video)' },
        { name: 'sweep_2', type: 'File', required: true, description: 'Sweep 2 file' },
        { name: 'sweep_3', type: 'File', required: true, description: 'Sweep 3 file' },
        { name: 'sweep_4', type: 'File', required: true, description: 'Sweep 4 file' },
        { name: 'sweep_5', type: 'File', required: true, description: 'Sweep 5 file' },
        { name: 'sweep_6', type: 'File', required: true, description: 'Sweep 6 file' },
        { name: 'sweep_7', type: 'File', required: true, description: 'Sweep 7 file' }
      ],
      responses: [
        { status: 201, description: 'Exam and 7 sweeps uploaded; inference submitted automatically.', example: '{ "aas_bs_exam_id": "uuid", "sweep_ids": ["uuid", ...] }' },
        { status: 400, description: 'Missing/invalid payload or files' }
      ]
    },
    // {
    //   endpoint: '/exams/{aas_bs_exam_id}/submit_inference',
    //   method: 'POST',
    //   title: 'Submit Exam for Inference',
    //   description: 'Submit an exam (created via upload_exam) for blind sweep inference. Locks the exam and enqueues 7 inference jobs. Use after upload_exam.',
    //   parameters: [
    //     { name: 'aas_bs_exam_id', type: 'UUID', required: true, description: 'ID of the exam (from upload_exam response)' }
    //   ],
    //   responses: [
    //     { status: 200, description: 'Inference submitted for all 7 sweeps', example: '{ "aas_bs_exam_id": "uuid", "message": "Inference submitted for all 7 sweeps." }' },
    //     { status: 400, description: 'Exam locked or invalid sweeps state' },
    //     { status: 404, description: 'Exam not found' }
    //   ]
    // },
    // {
    //   endpoint: '/new_exam',
    //   method: 'POST',
    //   title: 'Create New Exam (legacy)',
    //   description: 'Create a new exam for legacy one-by-one sweep upload.',
    //   parameters: [],
    //   responses: [
    //     { status: 201, description: 'Exam created successfully', example: '{ "id": "uuid" }' },
    //     { status: 400, description: 'Invalid request' }
    //   ]
    // },
    // {
    //   endpoint: '/upload',
    //   method: 'POST',
    //   title: 'Upload Sweep File (legacy)',
    //   description: 'Upload a single sweep file to an existing exam (legacy flow).',
    //   parameters: [
    //     { name: 'aas_bs_exam_id', type: 'string', required: true, description: 'ID of the exam to upload to' },
    //     { name: 'sweep_index', type: 'int', required: true, description: 'Index of the sweep (>0)' },
    //     { name: 'file', type: 'File', required: true, description: 'Sweep file (DICOM, image, or video)' },
    //     { name: 'pixel_spacing', type: 'float', required: false, description: 'Pixel spacing value (required for non-DICOM files)' },
    //     { name: 'pixel_spacing_unit', type: 'enum', required: false, description: 'Unit for pixel spacing (cm or mm, default: mm)' },
    //     { name: 'begin_process', type: 'boolean', required: false, description: 'Start processing immediately (default: false)' }
    //   ],
    //   responses: [
    //     { status: 200, description: 'Sweep uploaded successfully', example: '{ "sweep_id": "uuid", "status": "uploaded" }' },
    //     { status: 400, description: 'Invalid file or parameters' },
    //     { status: 404, description: 'Exam not found' }
    //   ]
    // },
    {
      endpoint: '/reports/{aas_bs_exam_id}',
      method: 'GET',
      title: t('endpoints.reports.title'),
      description: t('endpoints.reports.description'),
      parameters: [
        { name: 'aas_bs_exam_id', type: 'UUID', required: true, description: 'ID of the exam to retrieve' }
      ],
      responses: [
        { status: 200, description: 'Report retrieved successfully', example: '{ "aas_bs_exam_id": "uuid", "status": "completed", "results": {...} }' },
        { status: 404, description: 'Exam not found' },
        { status: 202, description: 'Processing still in progress' }
      ]
    }
  ], [t]);

  const renderEndpointCard = (endpoint: any, isBlindSweeps: boolean = false) => {
    let endpointKey = endpoint.endpoint.replace('/', '');
    // Handle path parameters like {aas_bs_exam_id}
    if (endpointKey.includes('{')) {
      endpointKey = endpointKey.split('{')[0]; // Get 'reports' from 'reports{aas_bs_exam_id}'
    }
    endpointKey = endpointKey.replace(/\/$/, ''); // Remove trailing slash
    const isExpanded = expandedTests[endpointKey] || false;
    
    // Build display path - blind sweeps get /aas_bs prefix, process endpoints get /aas prefix
    const displayPath = isBlindSweeps 
      ? `/api/v1/aas_bs${endpoint.endpoint}`
      : `/api/v1/aas${endpoint.endpoint}`;
    
    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-2 py-1 text-xs font-semibold rounded ${
                endpoint.method === 'GET' 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
        }`}>
          {endpoint.method}
        </span>
              <code className="text-sm font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
          {displayPath}
        </code>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {endpoint.title}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              {endpoint.description}
            </p>
          </div>
      </div>
      
        {/* Parameters and Responses - Enhanced Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Parameters */}
          {endpoint.parameters && endpoint.parameters.length > 0 && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <button
                onClick={() => toggleSectionExpansion(`${endpointKey}-parameters`)}
                className="w-full flex items-center justify-between p-5 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors rounded-xl"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">{t('card.parameters')}</h4>
                  <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                    {t('card.paramCount', { count: endpoint.parameters.length })}
                  </span>
                </div>
                <div className={`transform transition-transform duration-200 ${
                  expandedSections[`${endpointKey}-parameters`] ? 'rotate-180' : ''
                }`}>
                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              {expandedSections[`${endpointKey}-parameters`] && (
                <div className="px-5 pb-5">
                  <div className="space-y-3">
                    {endpoint.parameters.map((param: any, index: number) => (
                      <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-100 dark:border-blue-800/50">
                        <div className="flex items-start gap-3">
                          <code className="font-mono bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs min-w-0 flex-shrink-0">
                            {param.name}
                          </code>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                param.required 
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {param.required ? t('card.required') : t('card.optional')}
                              </span>
                              <span className="text-blue-600 dark:text-blue-400 text-xs font-medium bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded">
                                {param.type}
                              </span>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300 text-xs leading-relaxed">
                              {param.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Responses */}
          {endpoint.responses && endpoint.responses.length > 0 && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800">
              <button
                onClick={() => toggleSectionExpansion(`${endpointKey}-responses`)}
                className="w-full flex items-center justify-between p-5 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors rounded-xl"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <h4 className="text-sm font-semibold text-green-900 dark:text-green-100">{t('card.expectedResponses')}</h4>
                  <span className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                    {t('card.responseCount', { count: endpoint.responses.length })}
                  </span>
                </div>
                <div className={`transform transition-transform duration-200 ${
                  expandedSections[`${endpointKey}-responses`] ? 'rotate-180' : ''
                }`}>
                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              {expandedSections[`${endpointKey}-responses`] && (
                <div className="px-5 pb-5">
                  <div className="space-y-3">
                    {endpoint.responses.map((response: any, index: number) => (
                      <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-100 dark:border-green-800/50">
                        <div className="flex items-start gap-3">
                          <span className={`px-3 py-1 text-xs font-bold rounded min-w-0 flex-shrink-0 ${
                            response.status >= 200 && response.status < 300
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : response.status >= 400
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                          }`}>
                            {response.status}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-700 dark:text-gray-300 text-xs mb-2 leading-relaxed">
                              {response.description}
                            </p>
                            {response.example && (
                              <pre className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg text-xs overflow-x-auto border border-gray-200 dark:border-gray-700">
                                {response.example}
                              </pre>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Test it out button */}
        <div className="flex items-center justify-between pt-6 border-t-2 border-dashed border-gray-300 dark:border-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('card.readyToTest')}
            </span>
          </div>
          <button
            onClick={() => {
              setTestEndpoint(endpointKey);
              toggleTestExpansion(endpointKey);
            }}
            className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
              isExpanded
                ? 'bg-gray-600 text-white hover:bg-gray-700 shadow-lg'
                : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 shadow-md hover:shadow-lg'
            }`}
          >
            <FiPlay className="h-4 w-4" />
            {isExpanded ? t('card.hideTestForm') : t('card.testItOut')}
          </button>
        </div>

        {/* Test Form */}
        {isExpanded && (
          <div className="mt-6 p-6 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 rounded-xl border-2 border-amber-300 dark:border-amber-700 shadow-xl">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                <h4 className="text-xl font-bold text-amber-900 dark:text-amber-100">
                  🧪 Test This Endpoint
                </h4>
              </div>
              <p className="text-sm text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/30 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
                Fill in the required parameters and execute the request to see the real response from the API.
              </p>
            </div>
            {renderTestForm(endpointKey)}
          </div>
        )}
      </div>
    );
  };

  const segmentationTypes = useMemo(
    () => [
      {
        type: 'POLYLINE',
        description: t('segTypes.polyline.description'),
        properties: ['points', 'length', 'score'],
        example: t('segTypes.polyline.example'),
      },
      {
        type: 'FREEHAND',
        description: t('segTypes.freehand.description'),
        properties: ['points', 'area', 'perimeter', 'score'],
        example: t('segTypes.freehand.example'),
      },
      {
        type: 'ELLIPSE',
        description: t('segTypes.ellipse.description'),
        properties: ['points', 'area', 'perimeter', 'score'],
        example: t('segTypes.ellipse.example'),
      },
    ],
    [t]
  );

  const structureLabels = [
    { id: 0, name: 'Abdomen' },
    { id: 1, name: 'Amniotic Fluid' },
    { id: 2, name: 'Aorta' },
    { id: 3, name: 'Arm Humerus' },
    { id: 4, name: 'Cavum Septum Pellucidum' },
    { id: 5, name: 'Fetal Brain' },
    { id: 6, name: 'Cerebellum' },
    { id: 7, name: 'Cerebral Peduncle' },
    { id: 8, name: 'Cervix' },
    { id: 9, name: 'Choroid Plexus' },
    { id: 10, name: 'Cisterna Magna' },
    { id: 11, name: 'Femur Extremity' },
    { id: 12, name: 'Femur' },
    { id: 13, name: 'Fetal Bladder' },
    { id: 14, name: 'Fetal Kidneys' },
    { id: 15, name: 'Fetal Spine' },
    { id: 16, name: 'Foot' },
    { id: 17, name: 'Foramen Ovale' },
    { id: 18, name: 'Forearm Bones' },
    { id: 19, name: 'Genitalia' },
    { id: 20, name: 'Hand' },
    { id: 21, name: 'Hard Palate' },
    { id: 22, name: 'Heart' },
    { id: 23, name: 'Hippocampal Gyri' },
    { id: 24, name: 'Inferior Vena Cava' },
    { id: 25, name: 'Inter Atrial Septum' },
    { id: 26, name: 'Interventricular Septum' },
    { id: 27, name: 'Left Atrium' },
    { id: 28, name: 'Left Ventricle' },
    { id: 29, name: 'Leg Bones' },
    { id: 30, name: 'Lungs' },
    { id: 31, name: 'Mandible' },
    { id: 32, name: 'Maternal Bladder' },
    { id: 33, name: 'Mitral Valve' },
    { id: 34, name: 'Nasal Bone' },
    { id: 35, name: 'Nostrils' },
    { id: 36, name: 'Orbit' },
    { id: 37, name: 'Placenta' },
    { id: 38, name: 'Portal Sinus' },
    { id: 39, name: 'Pulmonary Artery' },
    { id: 40, name: 'Ribs' },
    { id: 41, name: 'Right Atrium' },
    { id: 42, name: 'Right Ventricle' },
    { id: 43, name: 'Stomach Bubble' },
    { id: 44, name: 'Superior Vena Cava' },
    { id: 45, name: 'Sylvian Fissure' },
    { id: 46, name: 'Thalamus' },
    { id: 47, name: 'Trachea' },
    { id: 48, name: 'Tricuspid Valve' },
    { id: 49, name: 'Umbilical Cord' },
    { id: 50, name: 'Umbilical Vein' },
    { id: 51, name: 'Upper Lip' },
    { id: 52, name: 'Ventricle' },
    { id: 53, name: 'Vermis' }
  ];

  return (
    <div className="h-full w-full">
      {/* Header */}
      <div className="mb-6 px-3">

        <p className="text-gray-600 dark:text-gray-400">
          {t('intro')}
        </p>
      </div>

      {/* Tabs */}
      <div className="px-3 mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('process')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'process'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('tabs.process')}
            </button>
            <button
              onClick={() => setActiveTab('blindsweeps')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'blindsweeps'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('tabs.blindSweeps')}
            </button>
            <button
              onClick={() => setActiveTab('segmentation')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'segmentation'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('tabs.segmentation')}
            </button>
            <button
              onClick={() => setActiveTab('structures')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'structures'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('tabs.structures')}
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="px-3 pb-6 h-[calc(100vh-200px)] overflow-y-auto">
        {activeTab === 'process' && (
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">{t('process.title')}</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('process.description')}
            </p>
            {processEndpoints.map((endpoint, index) => (
              <div key={index}>
                {renderEndpointCard(endpoint)}
                {index < processEndpoints.length - 1 && (
                  <div className="my-16 relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t-4 border-dashed border-gray-500 dark:border-gray-400"></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'blindsweeps' && (
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">{t('blindSweeps.title')}</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('blindSweeps.description')}
            </p>
            {blindSweepsEndpoints.map((endpoint, index) => (
              <div key={index}>
                {renderEndpointCard(endpoint, true)}
                {index < blindSweepsEndpoints.length - 1 && (
                  <div className="my-16 relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t-4 border-dashed border-gray-500 dark:border-gray-400"></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'segmentation' && (
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">{t('segmentation.title')}</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('segmentation.description')}
            </p>

            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">{t('segmentation.typesTitle')}</h3>
              <div className="grid gap-4">
                {segmentationTypes.map((type, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h4 className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-2">{type.type}</h4>
                    <p className="text-gray-700 dark:text-gray-300 mb-3">{type.description}</p>
                    <div className="mb-3">
                      <strong className="text-gray-900 dark:text-white">{t('segmentation.properties')}</strong>
                      <ul className="list-disc list-inside ml-4 mt-1">
                        {type.properties.map((prop, i) => (
                          <li key={i} className="text-sm text-gray-600 dark:text-gray-400">{prop}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded">
                      <strong className="text-gray-900 dark:text-white">{t('segmentation.exampleUse')}</strong> <span className="text-gray-700 dark:text-gray-300">{type.example}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Example Response */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">{t('segmentation.exampleResponseTitle')}</h3>
              <div className="bg-gray-900 dark:bg-gray-950 text-green-400 dark:text-green-300 p-4 rounded-lg overflow-x-auto">
                <pre className="text-sm">
{`{
  "frames": {
    "40": {
      "segmentations": {
        "1": {
          "type": "POLYLINE",
          "points": "499.00000\\\\276.00000,499.00000\\\\196.00000",
          "area": null,
          "perimeter": null,
          "length": 80,
          "score": 0.5119806528091431
        },
        "8": {
          "type": "FREEHAND",
          "points": "529\\\\310,528\\\\311,525\\\\311...",
          "area": 10107,
          "perimeter": 420.9604573249817,
          "length": null,
          "score": 0.37242981791496277
        }
      },
      "views": ["VIEW__SagittalViewOfCervixWithPlacenta"],
      "quality_metrics": {
        "VIEW__SagittalViewOfCervixWithPlacenta": {
          "QUAL_FLAG_VISIBLE_8": true,
          "QUAL_FLAG_VISIBLE_32": true
        }
      }
    }
  },
  "metadata": {
    "total_frames": 124,
    "total_unique_views": 2,
    "total_unique_structures": 8
  }
}`}
                </pre>
              </div>
            </div>

            {/* Field Explanations */}
            <div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">{t('segmentation.fieldExplanationsTitle')}</h3>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="grid gap-4">
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">points</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('fields.points')}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">area</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('fields.area')}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">perimeter</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('fields.perimeter')}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">length</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('fields.length')}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">score</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('fields.score')}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">views</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('fields.views')}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">quality_metrics</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('fields.qualityMetrics')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'structures' && (
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">{t('structures.title')}</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('structures.description')}
            </p>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {structureLabels.map((structure) => (
                  <div key={structure.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <span className="font-mono text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 px-2 py-1 rounded">
                      {structure.id}
                    </span>
                    <span className="text-sm text-gray-700 dark:text-gray-300 ml-3 flex-1">{structure.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-400 dark:border-blue-500 p-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>Note:</strong> {t('structures.note')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DocsPage; 


